/** Last applied API theme — applied before Angular boot to avoid default flash. */
export const STUDENT_THEME_CACHE_KEY = 'saferoads-student-theme-v1';

export interface StudentThemeCache {
  tokens: Record<string, string>;
  siteLogoUrl?: string | null;
  homeLetterLogoUrl?: string | null;
}

export function readCachedStudentTheme(): StudentThemeCache | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(STUDENT_THEME_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const cache = parsed as StudentThemeCache;
    if (!cache.tokens || typeof cache.tokens !== 'object') {
      return null;
    }
    return cache;
  } catch {
    return null;
  }
}

export function writeCachedStudentTheme(cache: StudentThemeCache): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(STUDENT_THEME_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Soft pale wash from primary — hue follows the brand:
 * - Green/teal (e.g. #274244) → mint (#eaffe8) via green-channel bias
 * - Blue / other (e.g. #004990) → soft blue (~#F5F8FC) via mix toward white
 */
export function derivePaleFromPrimary(
  primaryHex: string,
  opts: { mix?: number; greenBoost?: number; redPull?: number; bluePull?: number } = {}
): string | null {
  const rgb = parseHexRgb(primaryHex);
  if (!rgb) {
    return null;
  }
  const greenFamily = isGreenFamilyPrimary(rgb);
  // Green: 0.08 → ~#eaffe8. Blue/other: 0.04 → ~#F5F8FC for #004990.
  const mix = opts.mix ?? (greenFamily ? 0.08 : 0.04);

  if (!greenFamily) {
    return mixTowardWhite(primaryHex, 1 - mix);
  }

  const greenBoost = opts.greenBoost ?? 15;
  const redPull = opts.redPull ?? 4;
  const bluePull = opts.bluePull ?? 8;
  let r = Math.round(rgb.r * mix + 255 * (1 - mix));
  let g = Math.round(rgb.g * mix + 255 * (1 - mix));
  let b = Math.round(rgb.b * mix + 255 * (1 - mix));
  g = Math.min(255, g + greenBoost);
  r = Math.max(0, Math.min(255, r - redPull));
  b = Math.max(0, Math.min(255, b - bluePull));
  return rgbToHex(r, g, b);
}

/** Mid chip/accent from primary (mint for green brand; soft tint for blue/other). */
export function deriveAccentFromPrimary(primaryHex: string): string | null {
  return derivePaleFromPrimary(primaryHex, {
    mix: 0.38,
    greenBoost: 22,
    redPull: 6,
    bluePull: 10,
  });
}

/** Green–teal brands use mint washes; blue/others keep primary hue. */
function isGreenFamilyPrimary(rgb: { r: number; g: number; b: number }): boolean {
  const { h } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  return h >= 70 && h <= 200;
}

function rgbToHsl(
  r: number,
  g: number,
  b: number
): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) {
    return { h: 0, s: 0, l };
  }
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    default:
      h = ((r - g) / d + 4) / 6;
      break;
  }
  return { h: h * 360, s, l };
}

/** Mix hex toward white (no mint bias) — for mid/dark chrome shades. */
export function mixTowardWhite(hex: string, whiteAmount: number): string | null {
  const rgb = parseHexRgb(hex);
  if (!rgb) {
    return null;
  }
  const w = Math.min(1, Math.max(0, whiteAmount));
  return rgbToHex(
    rgb.r * (1 - w) + 255 * w,
    rgb.g * (1 - w) + 255 * w,
    rgb.b * (1 - w) + 255 * w
  );
}

/** Mix hex toward black. */
export function mixTowardBlack(hex: string, blackAmount: number): string | null {
  const rgb = parseHexRgb(hex);
  if (!rgb) {
    return null;
  }
  const k = Math.min(1, Math.max(0, blackAmount));
  return rgbToHex(rgb.r * (1 - k), rgb.g * (1 - k), rgb.b * (1 - k));
}

/**
 * Dark primary (L &lt; 0.42) → soften for dashboard/profile headers.
 * Light primary → keep full primary (softening would wash it out).
 */
export const PRIMARY_DARK_LIGHTNESS_MAX = 0.42;

export function isDarkPrimary(primaryHex: string): boolean {
  const rgb = parseHexRgb(primaryHex);
  if (!rgb) {
    return true;
  }
  return rgbToHsl(rgb.r, rgb.g, rgb.b).l < PRIMARY_DARK_LIGHTNESS_MAX;
}

/**
 * Chrome tokens for post-login headers / icons.
 * Always use full primary (no softening) so dashboard, profile, verify, etc. match brand.
 */
function syncAdaptiveChromeFromPrimary(
  primary: string,
  set: (name: string, value: string | null | undefined) => void,
  root: HTMLElement
): void {
  const dark = isDarkPrimary(primary);
  root.dataset['primaryTone'] = dark ? 'dark' : 'light';

  set('chrome', primary);
  set('chrome-mid', primary);
  // Slightly darker only for hover states
  set('chrome-soft', mixTowardBlack(primary, 0.08) ?? primary);
  set('chrome-icon', primary);
  set('chrome-icon-pale', primary);
  set('chrome-text', primary);
}

/** Apply backend token map to document CSS variables (safe before Angular bootstrap). */
export function applyStudentThemeTokens(tokens: Record<string, string>): void {
  if (typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  // Status / alert / palm-hand stay fixed in SCSS.
  // Theme chrome washes (light, accent, green-*) are derived from primary below.
  const skip = new Set([
    'success',
    'success-alt',
    'success-bright',
    'success-bootstrap',
    'success-flat',
    'success-flat-dark',
    'success-dark',
    'success-deeper',
    'success-mid',
    'success-bg',
    'success-bg-alt',
    'success-bg-soft',
    'success-bg-pale',
    'success-chip',
    'success-mint',
    'warning',
    'warning-alt',
    'warning-dark',
    'warning-text',
    'warning-amber',
    'warning-bg',
    'warning-bg-alt',
    'warning-bg-soft',
    'warning-chip',
    'warning-brown',
    'error',
    'error-alt',
    'error-bright',
    'error-flat',
    'error-test',
    'error-deep',
    'error-darker',
    'error-darkest',
    'error-border',
    'error-border-alt',
    'error-border-material',
    'error-bg-pink',
    'error-bg-soft',
    'error-bg-light',
    'error-bg-alert',
    'error-text-alert',
    'error-text-strong',
    'info',
    'info-alt',
    'info-link',
    'info-sky',
    'info-bg',
    'info-chip-bg',
    // FE derives these from primary (API LightenTowardWhite is too grey)
    'light',
    'accent',
    'accent-solid',
    'accent-green-mid',
    'accent-green-soft',
    'primary-light',
    'green-panel-bg',
    'green-chip-bg',
    'green-wash',
    'green-wash-alt',
    'green-wash-soft',
    'green-wash-mint',
    'green-highlight',
    'green-highlight-text',
    'green-dark-text',
    'green-forest',
    'green-olive',
    'green-leaf',
    'green-sage',
    'green-mid',
    'green-bright',
    'green-deep',
    'green-teal-flat',
    'green-teal-dark',
    'green-text-muted',
    'shadow-accent-30',
    'shadow-accent-40',
    // Adaptive header chrome (dark primary softens; light keeps full primary)
    'chrome',
    'chrome-mid',
    'chrome-soft',
    'chrome-icon',
    'chrome-icon-pale',
    'chrome-text',
  ]);
  for (const [name, value] of Object.entries(tokens)) {
    if (!name || value == null || value === '' || skip.has(name.toLowerCase())) {
      continue;
    }
    root.style.setProperty(`--saferoads-${name}`, value);
  }
  syncPrimaryRgb(tokens);
  syncPrimaryDarkRgb(tokens);
  syncSecondaryRgb(tokens);
  syncDerivedChromeFromPrimary(tokens);
  syncBrowserThemeColor(tokens);
}

/**
 * Derive theme-dependent chrome washes from primary.
 * Keeps success / warning / error / info / palm-hand / logout (error) as fixed SCSS.
 */
function syncDerivedChromeFromPrimary(tokens: Record<string, string>): void {
  const primary = tokens['primary']?.trim();
  if (!primary) {
    return;
  }
  const root = document.documentElement;

  const light = derivePaleFromPrimary(primary);
  const washSoft = derivePaleFromPrimary(primary, {
    mix: 0.04,
    greenBoost: 10,
    redPull: 2,
    bluePull: 5,
  });
  const wash = derivePaleFromPrimary(primary, {
    mix: 0.06,
    greenBoost: 12,
    redPull: 3,
    bluePull: 6,
  });
  const panel = derivePaleFromPrimary(primary, {
    mix: 0.1,
    greenBoost: 14,
    redPull: 4,
    bluePull: 7,
  });
  const chip = derivePaleFromPrimary(primary, {
    mix: 0.11,
    greenBoost: 14,
    redPull: 4,
    bluePull: 7,
  });
  const accentSoft = derivePaleFromPrimary(primary, {
    mix: 0.22,
    greenBoost: 20,
    redPull: 5,
    bluePull: 8,
  });
  const accent = deriveAccentFromPrimary(primary);
  const accentMid = derivePaleFromPrimary(primary, {
    mix: 0.48,
    greenBoost: 18,
    redPull: 6,
    bluePull: 10,
  });
  const primaryLight = accent;
  const tealFlat = mixTowardWhite(primary, 0.42);
  const tealDark = mixTowardWhite(primary, 0.22);
  const greenMid = mixTowardWhite(primary, 0.28);
  const greenBright = mixTowardWhite(primary, 0.32);
  const greenLeaf = mixTowardWhite(primary, 0.25);
  const greenSage = mixTowardWhite(primary, 0.3);
  const greenOlive = mixTowardWhite(primary, 0.2);
  const greenForest = mixTowardBlack(primary, 0.12);
  const greenDeep = mixTowardBlack(primary, 0.28);
  const highlightText = mixTowardBlack(primary, 0.35);
  const darkText = mixTowardBlack(primary, 0.45);
  const textMutedGreen = mixTowardWhite(primary, 0.15);

  const set = (name: string, value: string | null | undefined) => {
    if (value) {
      root.style.setProperty(`--saferoads-${name}`, value);
    }
  };

  set('light', light);
  set('accent', accent);
  set('accent-solid', accent);
  set('accent-green-mid', accentMid);
  set('accent-green-soft', accentSoft);
  set('primary-light', primaryLight);
  set('green-panel-bg', panel);
  set('green-chip-bg', chip);
  set('green-wash', wash);
  set('green-wash-alt', wash);
  set('green-wash-soft', washSoft);
  set('green-wash-mint', washSoft);
  set('green-highlight', wash);
  set('green-highlight-text', highlightText);
  set('green-dark-text', darkText);
  set('green-forest', greenForest);
  set('green-olive', greenOlive);
  set('green-leaf', greenLeaf);
  set('green-sage', greenSage);
  set('green-mid', greenMid);
  set('green-bright', greenBright);
  set('green-deep', greenDeep);
  set('green-teal-flat', tealFlat);
  set('green-teal-dark', tealDark);
  set('green-text-muted', textMutedGreen);
  // text-primary / text-secondary come from API (Primary text + Hint) — do not override

  syncAdaptiveChromeFromPrimary(primary, set, root);

  const accentRgb = accent ? parseHexRgb(accent) : null;
  if (accentRgb) {
    root.style.setProperty(
      '--saferoads-shadow-accent-30',
      `rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.3)`
    );
    root.style.setProperty(
      '--saferoads-shadow-accent-40',
      `rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.4)`
    );
  }
}

function syncSecondaryRgb(tokens: Record<string, string>): void {
  const secondary = tokens['secondary']?.trim();
  if (!secondary) {
    return;
  }
  const rgb = hexToRgbTriplet(secondary);
  if (rgb) {
    document.documentElement.style.setProperty('--saferoads-secondary-rgb', rgb);
  }
}

/**
 * Update every <link rel="icon"> and <link rel="apple-touch-icon"> in the
 * document to point at the supplied URL so the browser tab favicon always
 * reflects the currently uploaded home-letter logo rather than the build-time static file.
 */
export function updateFavicon(url: string): void {
  if (typeof document === 'undefined' || !url?.trim()) {
    return;
  }
  const selectors = [
    'link[rel="icon"]',
    'link[rel="shortcut icon"]',
    'link[rel="apple-touch-icon"]',
  ];
  selectors.forEach((sel) => {
    document.querySelectorAll<HTMLLinkElement>(sel).forEach((el) => {
      el.href = url;
    });
  });
}

export function applyHomeLetterLogoCssVar(resolvedUrl: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const safe = resolvedUrl.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  document.documentElement.style.setProperty(
    '--saferoads-home-letter-logo',
    `url("${safe}")`
  );
}

function syncPrimaryRgb(tokens: Record<string, string>): void {
  const primary = tokens['primary']?.trim();
  if (!primary) {
    return;
  }
  const rgb = hexToRgbTriplet(primary);
  if (rgb) {
    document.documentElement.style.setProperty('--saferoads-primary-rgb', rgb);
  }
}

function syncPrimaryDarkRgb(tokens: Record<string, string>): void {
  const dark =
    tokens['primary-dark']?.trim() ||
    tokens['dark']?.trim() ||
    tokens['text-primary']?.trim();
  if (!dark) {
    return;
  }
  const rgb = hexToRgbTriplet(dark);
  if (rgb) {
    document.documentElement.style.setProperty('--saferoads-primary-dark-rgb', rgb);
  }
}

function syncBrowserThemeColor(tokens: Record<string, string>): void {
  const isPlainColor = (v?: string) => !!v && /^(#|rgb|hsl)/i.test(v.trim());
  const header = tokens['header-background'];
  const color = (isPlainColor(header) ? header : tokens['primary'])?.trim();
  if (!isPlainColor(color) || typeof document === 'undefined') {
    return;
  }
  // Safari ignores in-place content changes on theme-color; replace the tag so the tab bar repaints.
  document.querySelectorAll('meta[name="theme-color"]').forEach((el) => el.remove());
  const meta = document.createElement('meta');
  meta.name = 'theme-color';
  meta.content = color as string;
  document.head.appendChild(meta);
}

function parseHexRgb(hex: string): { r: number; g: number; b: number } | null {
  let normalized = hex.replace('#', '').trim();
  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (normalized.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((x) => Math.round(x).toString(16).padStart(2, '0'))
      .join('')
  );
}

function hexToRgbTriplet(hex: string): string | null {
  const rgb = parseHexRgb(hex);
  return rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : null;
}
